/**
 * BudgetTab — per-descendant-segment budget vs. actual rollup.
 * Shows budget_hours, logged hours, %, and color-coded status.
 */
import { Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody, LinearProgress, Chip } from '@mui/material';
import { useProjectBudgetRollup } from '../../hooks/useProjects';

function pctColor(pct) {
  if (pct == null) return 'default';
  if (pct < 0.8) return 'success';
  if (pct < 1.0) return 'warning';
  return 'error';
}

function pctLabel(pct) {
  if (pct == null) return '—';
  return `${Math.round(pct * 100)}%`;
}

export default function BudgetTab({ projectId }) {
  const { data, isLoading } = useProjectBudgetRollup(projectId);
  const rows = (data?.rows || []).filter((r) => r.kind === 'segment');

  if (isLoading) return <LinearProgress />;
  if (!rows.length) return (
    <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
      No segments under this node yet. Add segments via the tree menu.
    </Typography>
  );

  const totalBudget = rows.reduce((s, r) => s + (r.budget_hours || 0), 0);
  const totalLogged = rows.reduce((s, r) => s + (r.logged_hours || 0), 0);

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="caption" color="text.secondary">Subtree totals</Typography>
        <Typography variant="h6">
          {totalLogged.toFixed(1)} / {totalBudget.toFixed(1)} h
          {totalBudget > 0 && (
            <Chip
              size="small"
              sx={{ ml: 1 }}
              label={pctLabel(totalLogged / totalBudget)}
              color={pctColor(totalLogged / totalBudget)}
            />
          )}
        </Typography>
      </Paper>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Segment</TableCell>
              <TableCell align="right">Budget (hrs)</TableCell>
              <TableCell align="right">Logged</TableCell>
              <TableCell align="right">%</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{r.name}</TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {r.budget_hours != null ? r.budget_hours.toFixed(1) : '—'}
                </TableCell>
                <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {r.logged_hours.toFixed(1)}
                </TableCell>
                <TableCell align="right">
                  <Chip size="small" label={pctLabel(r.pct_used)} color={pctColor(r.pct_used)} />
                </TableCell>
                <TableCell>{r.status}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
